/**
 * Pure validation functions for Client Booking Settings edit mode.
 */

/**
 * Validate weekly availability hours for overlapping time slots.
 * @param {Object} weeklyHours - { Monday: [{start, end}, ...], ... }
 * @returns {Object} { [day]: Set<overlapping slot indices> } or {}
 */
export function validateAvailability(weeklyHours) {
  const errors = {};

  for (const [day, slots] of Object.entries(weeklyHours)) {
    if (!slots || slots.length === 0) continue;

    const invalid = new Set();

    // Check each slot: end must be after start
    // A slot with start >= end is invalid and conflicts with all others
    const invertedSlots = new Set();
    for (let i = 0; i < slots.length; i++) {
      if (slots[i].start >= slots[i].end) {
        invertedSlots.add(i);
      }
    }

    // If any slot is inverted, mark it and all other slots as conflicting
    if (invertedSlots.size > 0 && slots.length > 1) {
      for (let i = 0; i < slots.length; i++) {
        invalid.add(i);
      }
    } else if (invertedSlots.size > 0) {
      // Single inverted slot — still flag it
      for (const idx of invertedSlots) {
        invalid.add(idx);
      }
    }

    // Check pairwise overlap
    for (let i = 0; i < slots.length; i++) {
      for (let j = i + 1; j < slots.length; j++) {
        const a = slots[i];
        const b = slots[j];
        if (a.start < b.end && b.start < a.end) {
          invalid.add(i);
          invalid.add(j);
        }
      }
    }

    if (invalid.size > 0) {
      errors[day] = invalid;
    }
  }

  return errors;
}

/**
 * Validate booking rules.
 * @param {Object} rules - { bufferTime, minNotice, bookingWindow }
 * @returns {Object} { bufferTime: "msg", ... } or {}
 */
export function validateBookingRules({ bufferTime, minNotice, bookingWindow }) {
  const errors = {};

  if (bufferTime !== "" && bufferTime != null) {
    const val = Number(bufferTime);
    if (isNaN(val) || val < 0) {
      errors.bufferTime = "Must be a non-negative number";
    } else if (val % 5 !== 0) {
      errors.bufferTime = "Must be a multiple of 5";
    }
  }

  if (minNotice !== "" && minNotice != null) {
    const val = Number(minNotice);
    if (isNaN(val) || val < 0) {
      errors.minNotice = "Must be a non-negative number";
    }
  }

  if (bookingWindow !== "" && bookingWindow != null) {
    const val = Number(bookingWindow);
    if (isNaN(val) || val < 0) {
      errors.bookingWindow = "Must be a non-negative number";
    }
  }

  return errors;
}

/**
 * Validate service config duration rules for overlapping sqft ranges.
 * @param {Array} durationRules - sorted by min_sqft
 * @returns {Object} { overlapping: Set<row indices> } or {}
 */
export function validateServiceConfig(durationRules) {
  if (!durationRules || durationRules.length < 2) return {};

  const sorted = [...durationRules].sort(
    (a, b) => (a.min_sqft || 0) - (b.min_sqft || 0)
  );

  const overlapping = new Set();
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (prev.max_sqft != null && curr.min_sqft <= prev.max_sqft) {
      overlapping.add(i - 1);
      overlapping.add(i);
    }
  }

  if (overlapping.size > 0) {
    return { overlapping };
  }
  return {};
}

/**
 * Run all validations.
 * @param {Object} formData - { weeklyHours, bufferTime, minNotice, bookingWindow, durationRules }
 * @returns {Object} { availability, bookingRules, serviceConfig, hasErrors }
 */
export function validateAll(formData) {
  const availability = validateAvailability(formData.weeklyHours || {});
  const bookingRules = validateBookingRules({
    bufferTime: formData.bufferTime,
    minNotice: formData.minNotice,
    bookingWindow: formData.bookingWindow,
  });
  const serviceConfig = validateServiceConfig(formData.durationRules);

  const hasErrors =
    Object.keys(availability).length > 0 ||
    Object.keys(bookingRules).length > 0 ||
    Object.keys(serviceConfig).length > 0;

  return { availability, bookingRules, serviceConfig, hasErrors };
}
